import { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { tokens } from "../../../theme";
import apiService from "../../../service/Api";
import { JobCardItem } from "./UpcomingJobCard";

// Router mounts assignments/proposals under job-cards/ alongside periods,
// cards, entries etc — see job_cards_router in urls.py.
const ASSIGNMENT_BASE = "/api/job-card/job-cards/assignments";
const PROPOSAL_BASE = "/api/job-card/job-cards/proposals";

interface Props {
  open: boolean;
  item: JobCardItem | null;
  onClose: () => void;
  onActionComplete?: () => void;
}

const JobCardItemDetailsModal = ({
  open,
  item,
  onClose,
  onActionComplete,
}: Props) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  // Reset local action state whenever a different item is opened.
  useEffect(() => {
    setSubmitting(false);
    setError(null);
    setIsRejecting(false);
    setRejectionReason("");
  }, [item?.id, open]);

  if (!item) return null;

  const statusColor =
    item.status === "approved"
      ? "#10b981"
      : item.status === "rejected"
        ? "#6b7280"
        : item.bucket === "overdue"
          ? "#E11D48"
          : item.bucket === "needs-review"
            ? "#3b82f6"
            : "#f59e0b";

  const statusLabel =
    item.status === "approved"
      ? "Approved"
      : item.status === "rejected"
        ? "Rejected"
        : item.bucket === "needs-review"
          ? "Needs Review"
          : item.bucket === "overdue"
            ? "Overdue"
            : "In Progress";

  // Only items sitting in "needs-review" are actually reviewable on the
  // backend (Assignment.REVIEWABLE_FROM = {"completed"}, FeatureProposal.
  // REVIEWABLE_FROM = {"pending"} — both map onto this bucket). Showing the
  // buttons anywhere else would just 400 against do_approve/do_reject.
  const isReviewable = item.bucket === "needs-review";
  const base = item.kind === "assignment" ? ASSIGNMENT_BASE : PROPOSAL_BASE;

  const handleApprove = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await apiService.post(`${base}/${item.rawId}/approve/`, {});
      onActionComplete?.();
      onClose();
    } catch {
      setError("Couldn't approve this item. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectClick = () => {
    if (!isRejecting) {
      setIsRejecting(true);
      return;
    }
    handleConfirmReject();
  };

  const handleConfirmReject = async () => {
    if (!rejectionReason.trim()) {
      setError("Please provide a reason for rejection.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await apiService.post(`${base}/${item.rawId}/reject/`, {
        rejection_reason: rejectionReason.trim(),
      });
      onActionComplete?.();
      onClose();
    } catch {
      setError("Couldn't reject this item. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: "20px",
          backgroundColor: colors.primary[900],
          border: `1px solid ${colors.primary[300]}30`,
          minWidth: 340,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.95rem",
            color: colors.primary[100],
          }}
        >
          {item.kind === "assignment"
            ? "Assignment Details"
            : "Proposal Details"}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon sx={{ fontSize: 16, color: colors.primary[600] }} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          pt: "8px !important",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: colors.primary[600],
              mb: 0.5,
              letterSpacing: "0.08em",
            }}
          >
            TITLE
          </Typography>
          <Typography
            sx={{
              fontSize: "0.9rem",
              fontWeight: 600,
              color: colors.primary[100],
            }}
          >
            {item.title}
          </Typography>
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: colors.primary[600],
              mb: 0.5,
              letterSpacing: "0.08em",
            }}
          >
            INTERN
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              src={item.internProfilePicture ?? undefined}
              sx={{
                width: 36,
                height: 36,
                border: `1.5px solid ${colors.primary[400]}`,
              }}
            >
              {item.internName?.[0]}
            </Avatar>
            <Typography
              sx={{ fontSize: "0.85rem", color: colors.primary[100] }}
            >
              {item.internName}
            </Typography>
          </Box>
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: colors.primary[600],
              mb: 0.5,
              letterSpacing: "0.08em",
            }}
          >
            DUE / DEADLINE
          </Typography>
          <Typography
            sx={{
              fontSize: "0.88rem",
              fontWeight: 600,
              color: colors.primary[100],
            }}
          >
            {item.dueDate
              ? new Date(item.dueDate).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })
              : "No date"}
          </Typography>
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: colors.primary[600],
              mb: 0.5,
              letterSpacing: "0.08em",
            }}
          >
            DESCRIPTION
          </Typography>
          <Typography sx={{ fontSize: "0.88rem", color: colors.primary[100] }}>
            {item.description || "—"}
          </Typography>
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: colors.primary[600],
              mb: 0.5,
              letterSpacing: "0.08em",
            }}
          >
            STATUS
          </Typography>
          <Chip
            label={statusLabel}
            size="small"
            sx={{
              backgroundColor: statusColor + "20",
              color: statusColor,
              fontWeight: 600,
            }}
          />
        </Box>

        {isReviewable && (
          <Box
            sx={{
              mt: 1,
              pt: 2,
              borderTop: `1px solid ${colors.primary[300]}15`,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            {isRejecting && (
              <TextField
                autoFocus
                multiline
                minRows={2}
                placeholder="Reason for rejection..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                disabled={submitting}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: "0.85rem",
                    color: colors.primary[100],
                  },
                }}
              />
            )}

            {error && (
              <Typography sx={{ fontSize: "0.75rem", color: "#E11D48" }}>
                {error}
              </Typography>
            )}

            <Box sx={{ display: "flex", gap: 1 }}>
              {isRejecting && (
                <Button
                  size="small"
                  onClick={() => {
                    setIsRejecting(false);
                    setRejectionReason("");
                    setError(null);
                  }}
                  disabled={submitting}
                  sx={{ color: colors.primary[600], textTransform: "none" }}
                >
                  Cancel
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={handleRejectClick}
                disabled={submitting}
                sx={{ textTransform: "none", ml: isRejecting ? 0 : "auto" }}
              >
                {submitting && isRejecting ? (
                  <CircularProgress size={16} sx={{ color: "#E11D48" }} />
                ) : isRejecting ? (
                  "Confirm Reject"
                ) : (
                  "Reject"
                )}
              </Button>
              {!isRejecting && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleApprove}
                  disabled={submitting}
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#10b981",
                    "&:hover": { backgroundColor: "#0ea371" },
                  }}
                >
                  {submitting ? (
                    <CircularProgress size={16} sx={{ color: "white" }} />
                  ) : (
                    "Accept"
                  )}
                </Button>
              )}
            </Box>
          </Box>
        )}

        <Box
          sx={{
            mt: isReviewable ? 0 : 2,
            pt: 1,
            borderTop: `1px solid ${colors.primary[300]}15`,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.8rem",
              fontWeight: 600,
              color: "#1976d2",
              textAlign: "center",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={() =>
              window.open(
                "https://jobcard.muzukuru.com/admin/dashboard",
                "_blank",
                "noopener,noreferrer",
              )
            }
          >
            View in Job Card
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default JobCardItemDetailsModal;
